import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ButtonComponent,
	LinkComponent,
	PwcsLayoutComponent,
	TileComponent,
	WatermarkComponent,
} from '@tecalisdev/infinity';
import { LoadingService } from 'src/app/core/services/loading.service';
import { LanguageService } from 'src/app/core/services/language.service';
import {
	IOperation,
	IProcessConfigResponse,
	OPERATION_TYPE,
	PROCESS_STATUS,
	ProcessLog,
} from 'src/app/user-identification/models/user-identification';
import { ProcessService } from 'src/app/user-identification/services/process.service';
import { CloudLogsService, LogType } from '../../services/cloud-logs.service';
import { ConfigService } from '../../services/config/config.service';
import { DesignService } from '../../services/config/design.service';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';

@Component({
	selector: 'app-delayed-process-warning',
	imports: [
		TranslatePipe,
		AsyncPipe,
		PwcsLayoutComponent,
		TileComponent,
		LinkComponent,
		ButtonComponent,
		WatermarkComponent,
	],
	templateUrl: './delayed-process-warning.component.html',
	styleUrl: './delayed-process-warning.component.scss',
})
export class DelayedProcessWarningComponent implements OnChanges, OnDestroy {
	@Input() process: IProcessConfigResponse;
	@Output() unSubscribeCheckStatus = new EventEmitter<void>();
	@Output() closed = new EventEmitter<void>();

	private processService = inject(ProcessService);
	private cloudLogsService = inject(CloudLogsService);
	private loadingService = inject(LoadingService);
	private trans = inject(TranslateService);
	public designService = inject(DesignService);
	private configService = inject(ConfigService);
	private languageService = inject(LanguageService);

	public title?: string;
	public subtitle?: string;
	/** UI de reanudación (icono refresh + paso); false = bienvenida al iniciar por primera vez. */
	public showResumeDetails = false;
	public notificationStatus?: 'OK' | 'KO';
	private destroy$ = new Subject<void>();

	constructor() {
		this.configService.config$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			if (this.process?.config?.delayed_process_warning) {
				this.updateTitle();
				this.updateSubtitle();
			}
		});
		this.trans.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
			if (this.process?.config?.delayed_process_warning) {
				this.updateTitle();
				this.updateSubtitle();
			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['process'] && this.process?.config?.delayed_process_warning) {
			this.updateTitle();
			this.updateSubtitle();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public sendNotification(): void {
		this.cloudLogsService.sendCloudLogs({ notifyInvalidProcess: 'preload' }, ProcessLog.IN_PROCESS, LogType.Error);
		this.loadingService.show(this.trans.instant('GENERAL.LOADING.NOTIFY_INVALID_PROCESS'));
		this.processService.notifyInvalidProcess().subscribe({
			next: () => {
				this.unSubscribeCheckStatus.emit();
				this.loadingService.hide();
				this.notificationStatus = 'OK';
				setTimeout(() => {
					this.process.status_process = PROCESS_STATUS.CANCELLED;
					this.process.status_process_device = PROCESS_STATUS.CANCELLED;
					this.process.config.delayed_process_warning = false;
					this.closed.emit();
				}, 3000);
			},
			error: () => {
				this.loadingService.hide();
				this.notificationStatus = 'KO';
			},
		});
	}

	public closeWarning(): void {
		this.process.config.delayed_process_warning = false;
		this.closed.emit();
	}

	private updateTitle(): void {
		this.showResumeDetails = this.hasProcessProgress();

		const customTitle = this.languageService.resolveLocalizedText(this.process.config.delayed_process_warning_text);
		if (customTitle) {
			this.title = customTitle;
			return;
		}

		const hasKycOperation = this.process?.operations?.some(
			(op) => op.type === OPERATION_TYPE.OCR || op.type === OPERATION_TYPE.POL,
		);
		const titlePrefix = this.showResumeDetails ? 'PAGE_CHANGE_DEVICE' : 'DELAYED_PROCESS_WARNING';

		this.title = this.trans.instant(`${titlePrefix}.TITLE_${hasKycOperation ? 'KYC_' : ''}DEFAULT`, {
			company: this.process?.company,
		});
	}

	private updateSubtitle(): void {
		if (!this.showResumeDetails) {
			this.subtitle = '';
			return;
		}

		const currentOperation = this.process?.operations?.find((op) => !op.completed);
		if (currentOperation) {
			const step = this.getOperationType(currentOperation);
			this.subtitle = this.trans.instant('PAGE_CHANGE_DEVICE.SUBTITLE', { step });
		} else {
			this.subtitle = '';
		}
	}

	/** Reanudación según `status_process === IN_PROGRESS` (paridad con UserIdentification / NIF-213). */
	private hasProcessProgress(): boolean {
		return this.process?.status_process === PROCESS_STATUS.INPROGRESS;
	}

	private getOperationType(op: IOperation): string {
		let type: string = op.type;
		// Si tiene evaluación financiera, se muestra ese tipo en lugar del tipo de operación
		if (op?.type === OPERATION_TYPE.DOCUMENTS || op?.type === OPERATION_TYPE.CONNECTIONS) {
			if (op?.config && 'evaluation' in op.config && op?.config?.evaluation) {
				type = 'FINANCIAL_EVALUATION';
			} else {
				type = OPERATION_TYPE.DOCUMENTS;
			}
		}
		return this.trans.instant(`PAGE_CHANGE_DEVICE.OPERATION_TYPE.${type}`);
	}
}
